/**
 * Hook para gerenciar instância Cytoscape.js
 * Baseado em pesquisa 2025: react-cytoscapejs patterns, CodeSandbox examples
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import cytoscape, { Core, EventObject, NodeSingular } from 'cytoscape';
import type { Investigation } from '../types/investigation';
import { apiToCytoscape, cytoscapeStylesheet, defaultLayoutConfig } from '../utils/graphHelpers';

interface UseGraphOptions {
  onNodeClick?: (node: NodeSingular) => void;
  onNodeTap?: (node: NodeSingular) => void; // Mobile tap
  onNodeHover?: (node: NodeSingular) => void;
  onBackgroundClick?: () => void;
}

interface UseGraphReturn {
  cyRef: React.MutableRefObject<Core | null>;
  containerRef: React.RefCallback<HTMLDivElement>;
  selectedNode: NodeSingular | null;
  loading: boolean;

  // Actions
  loadInvestigation: (investigation: Investigation) => void;
  fitGraph: () => void;
  resetZoom: () => void;
  centerNode: (nodeId: string) => void;
  highlightNeighbors: (nodeId: string) => void;
  clearHighlights: () => void;
  exportPNG: () => string | null;
}

/**
 * Hook customizado para gerenciar grafo Cytoscape.js
 *
 * Features:
 * - ✅ Lifecycle management do Cytoscape
 * - ✅ Ref-based para acesso direto à API
 * - ✅ Event handlers customizáveis
 * - ✅ Helpers para operações comuns (zoom, fit, export)
 * - ✅ Type-safe com TypeScript
 *
 * @param options - Callbacks para eventos do grafo
 * @returns Refs e funções para controlar o grafo
 *
 * @example
 * const { containerRef, loadInvestigation, fitGraph } = useGraph({
 *   onNodeClick: (node) => console.log('Clicked:', node.id())
 * });
 *
 * return <div ref={containerRef} style={{ width: '100%', height: '600px' }} />;
 */
export function useGraph(options: UseGraphOptions = {}): UseGraphReturn {
  const {
    onNodeClick,
    onNodeTap,
    onNodeHover,
    onBackgroundClick,
  } = options;

  // Ref para armazenar instância Cytoscape (não causa re-render)
  const cyRef = useRef<Core | null>(null);

  // State para nó selecionado
  const [selectedNode, setSelectedNode] = useState<NodeSingular | null>(null);
  const [loading, setLoading] = useState(false);

  /**
   * Ref callback para o container do grafo
   * Inicializa Cytoscape quando o elemento é montado
   */
  const containerRef = useCallback(
    (container: HTMLDivElement | null) => {
      // Limpar instância anterior se existir
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }

      if (!container) return;

      // Criar nova instância Cytoscape
      const cy = cytoscape({
        container,
        elements: [], // Vazio inicialmente
        style: cytoscapeStylesheet,
        layout: { name: 'preset' }, // Sem layout inicial
        minZoom: 0.1,
        maxZoom: 3,
        wheelSensitivity: 0.2,

        // Otimizações de performance
        hideEdgesOnViewport: true,
        textureOnViewport: false,
        motionBlur: false,
        pixelRatio: 'auto',
      });

      // Salvar ref
      cyRef.current = cy;

      // ============ Event Listeners ============

      // Click em nó (desktop)
      cy.on('tap', 'node', (evt: EventObject) => {
        const node = evt.target as NodeSingular;
        setSelectedNode(node);
        onNodeClick?.(node);
        onNodeTap?.(node); // Funciona também para mobile
      });

      // Hover em nó (somente desktop, não funciona em touch)
      cy.on('mouseover', 'node', (evt: EventObject) => {
        const node = evt.target as NodeSingular;
        onNodeHover?.(node);

        // Visual feedback: cursor pointer
        container.style.cursor = 'pointer';
      });

      cy.on('mouseout', 'node', () => {
        container.style.cursor = 'default';
      });

      // Click no background (deselecionar)
      cy.on('tap', (evt: EventObject) => {
        if (evt.target === cy) {
          setSelectedNode(null);
          onBackgroundClick?.();
        }
      });

      // Context menu desabilitado (evitar conflito com right-click no browser)
      cy.on('cxttap', 'node', (evt: EventObject) => {
        evt.preventDefault();
      });

    },
    [onNodeClick, onNodeTap, onNodeHover, onBackgroundClick]
  );

  /**
   * Carrega investigação no grafo
   */
  const loadInvestigation = useCallback((investigation: Investigation) => {
    if (!cyRef.current) return;

    setLoading(true);

    try {
      const cy = cyRef.current;

      // Converter dados da API para formato Cytoscape
      const elements = apiToCytoscape(investigation);

      // Limpar grafo anterior
      cy.elements().remove();

      // Adicionar novos elementos
      cy.add(elements);

      // Aplicar layout automático
      const layout = cy.layout(defaultLayoutConfig);
      layout.run();

      // Ajustar zoom após layout completar
      layout.on('layoutstop', () => {
        cy.fit(undefined, 50); // 50px de padding
        setLoading(false);
      });

    } catch (error) {
      console.error('[useGraph] Error loading investigation:', error);
      setLoading(false);
    }
  }, []);

  /**
   * Ajusta zoom para mostrar todo o grafo
   */
  const fitGraph = useCallback(() => {
    if (!cyRef.current) return;
    cyRef.current.fit(undefined, 50);
  }, []);

  /**
   * Reseta zoom para 100%
   */
  const resetZoom = useCallback(() => {
    if (!cyRef.current) return;
    cyRef.current.zoom(1);
    cyRef.current.center();
  }, []);

  /**
   * Centraliza um nó específico
   */
  const centerNode = useCallback((nodeId: string) => {
    if (!cyRef.current) return;

    const node = cyRef.current.$id(nodeId);
    if (node.length > 0) {
      cyRef.current.animate({
        center: { eles: node },
        zoom: 1.5,
      }, {
        duration: 500,
        easing: 'ease-in-out-cubic',
      });
    }
  }, []);

  /**
   * Destaca um nó e seus vizinhos
   */
  const highlightNeighbors = useCallback((nodeId: string) => {
    if (!cyRef.current) return;

    const cy = cyRef.current;
    const node = cy.$id(nodeId);

    if (node.length === 0) return;

    // Todos os elementos
    const allElements = cy.elements();

    // Vizinhos (1º grau)
    const neighbors = node.neighborhood();

    // Diminuir opacidade de tudo
    allElements.addClass('faded');

    // Destacar nó e vizinhos
    node.removeClass('faded').addClass('highlighted');
    neighbors.removeClass('faded').addClass('highlighted');
  }, []);

  /**
   * Remove todos os destaques
   */
  const clearHighlights = useCallback(() => {
    if (!cyRef.current) return;

    cyRef.current.elements().removeClass('faded highlighted');
  }, []);

  /**
   * Exporta grafo como PNG (base64)
   */
  const exportPNG = useCallback((): string | null => {
    if (!cyRef.current) return null;

    try {
      const png = cyRef.current.png({
        output: 'base64uri',
        bg: '#ffffff',
        full: true,
        scale: 2, // 2x para melhor qualidade
      });
      return png;
    } catch (error) {
      console.error('[useGraph] Error exporting PNG:', error);
      return null;
    }
  }, []);

  /**
   * Cleanup ao desmontar componente
   */
  useEffect(() => {
    return () => {
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
    };
  }, []);

  return {
    cyRef,
    containerRef,
    selectedNode,
    loading,
    loadInvestigation,
    fitGraph,
    resetZoom,
    centerNode,
    highlightNeighbors,
    clearHighlights,
    exportPNG,
  };
}
